// -----------------------------------------------------------------------------
// Domain types for the admin surface.
//
// Derived from `Backend/src/types/domain.ts`, with two deliberate differences:
//
//   1. Firestore `Timestamp` becomes an ISO-8601 `string`. The API serialises it
//      that way and the panel has no Firestore SDK to rehydrate it with.
//   2. Only the fields the panel actually renders are modelled. A member's blood
//      reports, check-in photos and chat messages are ABSENT and must stay
//      absent - see the note on `MemberDetail`.
// -----------------------------------------------------------------------------

/** ISO-8601 with timezone, e.g. `2026-08-14T09:30:00.000Z`. */
export type IsoDateTime = string;

// -- Identity -----------------------------------------------------------------

export const Role = {
  USER: 'user',
  TRAINER: 'trainer',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
} as const;

export type Role = (typeof Role)[keyof typeof Role];

/** Matches `ROLE_RANK` in the backend. Sparse so a tier can be inserted. */
export const ROLE_RANK: Record<Role, number> = {
  [Role.USER]: 10,
  [Role.TRAINER]: 20,
  [Role.ADMIN]: 90,
  [Role.SUPER_ADMIN]: 100,
};

/** The two roles allowed to use this panel at all. */
export const STAFF_ROLES: readonly Role[] = [Role.ADMIN, Role.SUPER_ADMIN];

export function isStaffRole(role: string | null | undefined): role is 'admin' | 'super_admin' {
  return role === Role.ADMIN || role === Role.SUPER_ADMIN;
}

/**
 * "At least this privileged".
 *
 * SUPER_ADMIN inherits ADMIN, so every ADMIN capability is expressed once and
 * super admins pass it by rank. There is no `role === 'admin' || role ===
 * 'super_admin'` anywhere in this codebase - that pattern is how a permission
 * check ends up updated in four places and missed in the fifth.
 */
export function hasAtLeastRole(role: Role | null | undefined, minimum: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export const AccountStatus = {
  PENDING: 'pending',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  BLOCKED: 'blocked',
  DELETED: 'deleted',
} as const;

export type AccountStatus = (typeof AccountStatus)[keyof typeof AccountStatus];

export const TrainerApproval = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type TrainerApproval = (typeof TrainerApproval)[keyof typeof TrainerApproval];

export const ReviewStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type ReviewStatus = (typeof ReviewStatus)[keyof typeof ReviewStatus];

export type Gender = 'male' | 'female' | 'other' | 'unspecified';

// -- Session ------------------------------------------------------------------

/** `GET /auth/me`. */
export type AuthMe = {
  uid: string;
  role: Role;
  status: AccountStatus;
  onboardingCompleted: boolean;
  profileExists: boolean;
  onboardingSkipped: boolean;
  email: string | null;
  phoneNumber: string | null;
};

// -- People -------------------------------------------------------------------

/** A row in Members or Coaches. Deliberately small - this is list data. */
export type AdminUserSummary = {
  uid: string;
  role: Role;
  status: AccountStatus;
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string | null;
  photoURL: string | null;
  city: string | null;
  /**
   * Coach review state, or `null` for a member.
   *
   * ⚠ A DIFFERENT AXIS FROM `status`. `status` is the ACCOUNT - active,
   * suspended, blocked. `approval` is whether an admin has vetted this person
   * as a coach. Finishing onboarding sets the account `active` while approval
   * stays `pending`, so reading `status` alone reports an unreviewed coach as
   * "Active" - true of the account, and not the question the Coaches screen is
   * open to answer.
   */
  approval: TrainerApproval | null;
  createdAt: IsoDateTime;
  lastLoginAt: IsoDateTime | null;
};

/**
 * The member detail panel.
 *
 * NOTE ON SCOPE - this type is a privacy boundary, not just a shape. Blood
 * reports, weekly check-in photos, body measurements and chat contents are the
 * member's own data. Staff having a reason to look at an account is not a reason
 * to expose their health records, so those fields are not modelled here and must
 * not be added. If support genuinely needs one, it belongs behind an explicit,
 * separately-audited, reason-required flow - not on a detail tab.
 */
export type AdminUserDetail = AdminUserSummary & {
  gender: Gender | null;
  dateOfBirth: string | null;
  timezone: string;
  onboardingCompleted: boolean;
  referralCode: string | null;
  /** Reason recorded the last time an admin changed this account's status. */
  statusReason: string | null;
  statusChangedAt: IsoDateTime | null;
  statusChangedBy: string | null;
  counts: {
    connections: number;
    activeSubscriptions: number;
    payments: number;
  };
  subscription: {
    id: string;
    planName: string;
    status: SubscriptionStatus;
    currentPeriodEnd: IsoDateTime;
  } | null;
  connections: {
    id: string;
    counterpartyId: string;
    counterpartyName: string;
    status: string;
    startedAt: IsoDateTime | null;
  }[];
  activity: {
    lastWorkoutAt: IsoDateTime | null;
    lastNutritionLogAt: IsoDateTime | null;
    lastCheckinAt: IsoDateTime | null;
  };
  /**
   * The coach card, joined from `trainers/{uid}`.
   *
   * `null` for a member - the document does not exist for them - which is also
   * what the detail drawer switches on. A coach and a member are not the same
   * record with a different `role` string: a coach has a public profile, a
   * price list, a roster and a moderation history, and NONE of that has a
   * member equivalent to render in the same slots.
   */
  coach: CoachProfile | null;
};

/**
 * Everything the panel shows about a coach that a member has no version of.
 *
 * Assembled server-side from `trainers/{uid}` plus four aggregate queries -
 * roster, submissions and earnings are counts, not documents, so the join costs
 * one round trip and returns no client data.
 */
export type CoachProfile = {
  approval: TrainerApproval;
  /** Set by an admin when rejecting. The coach sees this text. */
  rejectionReason: string | null;

  expertise: string;
  bio: string;
  skills: string[];
  certifications: string[];
  experienceYears: number;
  experienceMonths: number;
  state: string;
  trainingModes: TrainingMode[];
  pricingPlans: PricingPlan[];

  /** Surfaces the coach in the member app's featured carousel. */
  featured: boolean;
  /**
   * The DISPLAYED rating: the admin's seed while `reviewCount` is 0, the earned
   * average once real reviews exist.
   */
  rating: number;
  reviewCount: number;
  clientCount: number;
  profileUpdatedAt: IsoDateTime | null;

  /**
   * The starting rating an admin set when approving this coach, and who set it.
   *
   * `null` means nobody seeded one. Kept separate from `rating` so the panel can
   * label a 4.5 as an editorial starting position rather than let staff read it
   * as something members produced - `reviewCount` stays 0 either way, because
   * the platform does not invent reviews to go with a seeded score.
   */
  seedRating: number | null;
  seedRatingBy: string | null;
  /** `seedRatingBy` resolved to a name. Falls back to the uid. */
  seedRatingByName: string | null;
  seedRatingAt: IsoDateTime | null;
  seedRatingNote: string | null;

  /** Their roster, by connection status. */
  roster: {
    active: number;
    pending: number;
    paused: number;
    ended: number;
  };

  /** Their own submissions in the moderation queue. */
  submissions: {
    pending: number;
    approved: number;
    rejected: number;
  };

  /** Rupees. Lifetime successful payments attributed to this coach. */
  earnings: {
    lifetime: number;
    payments: number;
  };
};

// -- Coach applications -------------------------------------------------------

export type PlanDuration = '1month' | '3month' | '6month';
export type TrainingMode = 'online' | 'offline' | 'hybrid';

export type PricingPlan = {
  duration: PlanDuration;
  /** Onboarding-written tiers may carry the older `'gym'` for Offline. */
  mode: TrainingMode | 'gym' | null;
  sessionsPerMonth: number;
  /** Rupees. Integer - money is never a float. */
  price: number;
  /** The coach's own label from Manage Plans. Absent/null -> "N Month Plan". */
  name?: string | null;
  benefits?: string[];
  /** False when the coach has switched the tier off. Absent means offered. */
  active?: boolean;
};

/** A row in the Coach Applications queue. */
export type CoachApplicationSummary = {
  uid: string;
  firstName: string;
  lastName: string;
  photoURL: string | null;
  expertise: string;
  city: string;
  state: string;
  approval: TrainerApproval;
  /** FIFO ordering key - oldest first, so nobody starves at the bottom. */
  submittedAt: IsoDateTime;
  certificateCount: number;
  transformationCount: number;
  mediaCount: number;
};

export type CertificateItem = {
  id: string;
  name: string;
  issuer: string;
  issuedOn: string;
  fileType: 'pdf' | 'image' | 'doc';
  fileUrl: string | null;
  status: ReviewStatus;
  submittedAt: IsoDateTime;
};

export type TransformationItem = {
  id: string;
  title: string;
  clientName: string;
  description: string;
  beforeImageUrl: string | null;
  afterImageUrl: string | null;
  status: ReviewStatus;
  submittedAt: IsoDateTime;
};

export type MediaItem = {
  id: string;
  type: 'photo' | 'video';
  category: 'photo' | 'video' | 'story' | 'highlight';
  url: string;
  thumbnailUrl: string | null;
  status: ReviewStatus;
  submittedAt: IsoDateTime;
};

export type CoachApplicationDetail = CoachApplicationSummary & {
  /** Set at approval. `null` on a pending or unseeded application. */
  seedRating: number | null;
  seedRatingNote: string | null;
  email: string | null;
  phoneNumber: string | null;
  bio: string;
  skills: string[];
  certifications: string[];
  experienceYears: number;
  experienceMonths: number;
  trainingModes: TrainingMode[];
  pricingPlans: PricingPlan[];
  rejectionReason: string | null;
  certificates: CertificateItem[];
  transformations: TransformationItem[];
  media: MediaItem[];
};

// -- Moderation ---------------------------------------------------------------

export const ModerationType = {
  CERTIFICATE: 'certificate',
  TRANSFORMATION: 'transformation',
  MEDIA: 'media',
  TRAINER: 'trainer',
} as const;

export type ModerationType = (typeof ModerationType)[keyof typeof ModerationType];

/**
 * One row of the unified review inbox.
 *
 * The backend writes a thin pointer document alongside each submission
 * (`ModerationQueueDoc`) precisely so this inbox is one query instead of three
 * collection-group reads across every trainer.
 */
export type ModerationItem = {
  id: string;
  type: ModerationType;
  trainerId: string;
  trainerName: string;
  trainerPhotoURL: string | null;
  status: ReviewStatus;
  submittedAt: IsoDateTime;
  reviewedBy: string | null;
  /**
   * `reviewedBy` resolved to a person, joined server-side.
   *
   * History exists to answer "who decided this", and a 28-character Firebase
   * uid does not answer it. Falls back to the uid rather than to null when the
   * reviewer's account no longer resolves - a deleted admin still has to be
   * traceable.
   */
  reviewedByName: string | null;
  reviewedAt: IsoDateTime | null;
  /** What the reviewer actually looks at. */
  preview: {
    title: string;
    subtitle: string | null;
    /** `image` and `pdf` render inline; `none` gets a metadata-only card. */
    kind: 'image' | 'pdf' | 'video' | 'none';
    url: string | null;
    secondaryUrl: string | null;
  };
};

// -- Commerce -----------------------------------------------------------------

export const SubscriptionStatus = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  PAUSED: 'paused',
} as const;

export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export type SubscriptionRow = {
  id: string;
  userId: string;
  userName: string;
  planId: string;
  planName: string;
  trainerId: string | null;
  trainerName: string | null;
  status: SubscriptionStatus;
  /** Rupees, integer. */
  amount: number;
  startedAt: IsoDateTime;
  currentPeriodEnd: IsoDateTime;
  cancelledAt: IsoDateTime | null;
};

export type PaymentStatus = 'success' | 'pending' | 'failed' | 'refunded';

export type PaymentRow = {
  id: string;
  userId: string;
  userName: string;
  payee: string;
  purpose: string;
  /** Rupees, always a positive integer. `direction` carries the sign. */
  amount: number;
  currency: 'INR';
  status: PaymentStatus;
  direction: 'debit' | 'credit';
  method: 'upi' | 'card' | 'wallet' | 'netbanking';
  category: 'coaching' | 'plan' | 'nutrition' | 'store' | 'subscription';
  gatewayPaymentId: string | null;
  paidAt: IsoDateTime;
  refundedAt: IsoDateTime | null;
  refundedBy: string | null;
  /**
   * Captured but refused (one-plan rule, a closed coach, an amount mismatch),
   * so the money is owed back. Shown as "Refund needed · coming soon" - there
   * is no action for it until refunds launch.
   */
  requiresRefund: boolean;
  /** `plan_conflict` | `coach_account_closed` | `amount_mismatch`, or null. */
  failureReason: string | null;
};

// -- App plans ----------------------------------------------------------------

/**
 * One of the app's OWN plans - the "SectFit smart plans" catalogue.
 *
 * Mirrors `AdminPlanRow` in `Backend/src/modules/admin/admin.commerce.service.ts`.
 * `perMonthPrice` and `discountPercent` are DERIVED server-side and read-only
 * here: they exist so the panel can show what a price change does before it is
 * saved, not as separate values to edit.
 */
export type AdminPlanRow = {
  id: string;
  name: string;
  /** Whole rupees. What the next Razorpay order will be built from. */
  price: number;
  /** Struck-through "was" price. Must be >= `price`. */
  originalPrice: number;
  durationMonths: number;
  /** Derived: `floor(price / durationMonths)`. */
  perMonthPrice: number;
  /** Derived: `floor((originalPrice - price) / originalPrice * 100)`. */
  discountPercent: number;
  features: string[];
  badge: 'popular' | null;
  displayOrder: number;
  /** False renders the app's "currently full" state and blocks purchase. */
  available: boolean;
  /** False removes it from the app's catalogue entirely. */
  active: boolean;
  updatedAt: IsoDateTime | null;
};

export type UpdatePlanBody = Partial<{
  name: string;
  price: number;
  originalPrice: number;
  durationMonths: number;
  features: string[];
  badge: 'popular' | null;
  displayOrder: number;
  available: boolean;
  active: boolean;
}>;

// -- Support ------------------------------------------------------------------

export const TicketStatus = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

export type SupportTicketSummary = {
  id: string;
  uid: string;
  userName: string;
  userRole: Role;
  subject: string;
  status: TicketStatus;
  assigneeUid: string | null;
  assigneeName: string | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
};

export type TicketReply = {
  id: string;
  authorUid: string;
  authorName: string;
  authorIsStaff: boolean;
  body: string;
  createdAt: IsoDateTime;
};

export type SupportTicketDetail = SupportTicketSummary & {
  message: string;
  replies: TicketReply[];
};

// -- Staff --------------------------------------------------------------------

export type StaffMember = {
  uid: string;
  /** Immutable from the panel - it is the login and where a 2FA code is sent. */
  email: string;
  /** E.164, or null. Once set, it is an alternative sign-in identifier. */
  phoneNumber: string | null;
  firstName: string;
  lastName: string;
  photoURL: string | null;
  role: 'admin' | 'super_admin';
  status: AccountStatus;
  createdAt: IsoDateTime;
  lastLoginAt: IsoDateTime | null;
  invitedBy: string | null;
};

// -- Staff sign-in ------------------------------------------------------------

/**
 * What `POST /auth/staff/challenge` returns once a password is accepted.
 *
 * Deliberately carries NO token. A session does not exist until the one-time
 * code is verified - that is the whole point of the design, and it is why this
 * shape has nothing in it that a browser could use.
 */
export type StaffChallenge = {
  challengeId: string;
  /** ISO. The panel counts down against it. */
  expiresAt: IsoDateTime;
  /** `an***@sect.fit` - enough to confirm the right inbox, not to harvest one. */
  sentTo: string;
  resendAvailableInSeconds: number;
};

// -- Audit --------------------------------------------------------------------

export type AuditLogEntry = {
  id: string;
  actorUid: string;
  actorName: string;
  actorRole: Role;
  action: string;
  resourceType: string;
  resourceId: string;
  metadata: Record<string, unknown>;
  ip: string | null;
  requestId: string;
  createdAt: IsoDateTime;
};

// -- Configuration ------------------------------------------------------------

export type PlatformConfig = {
  maintenanceMode: boolean;
  maintenanceMessage: string;
  /** Semver. Clients below this are forced to update. */
  minSupportedVersion: string;
  featureFlags: Record<string, boolean>;
  /** Percentage the platform takes from a coach subscription. Integer 0–100. */
  commissionPercent: number;
  /** Rupees. Refunds above this need a second approver. */
  refundApprovalThreshold: number;
  updatedAt: IsoDateTime;
  updatedBy: string | null;
};

/** The catalogue Add Certificate and Trainer Onboarding both render on the
 *  mobile app. `appConfig/certificateTypes`, ADMIN-editable (not SUPER_ADMIN
 *  like `PlatformConfig` - see `Backend/src/modules/admin/admin.routes.ts`). */
export type CertificateTypesConfig = {
  /** Every name in the catalogue, enabled or not. */
  options: string[];
  /**
   * The names withheld from the mobile pickers - the alternative to deleting
   * a row. A coach who already holds a disabled certification keeps it; it
   * just stops being offered, and can be switched back on without retyping.
   *
   * Always a subset of `options`; the server drops anything else. See
   * `Backend/src/services/catalogue.ts`.
   */
  disabled: string[];
  updatedAt: IsoDateTime;
  updatedBy: string | null;
};

/** `appConfig/skills` - same shape as the certificate catalogue above. */
export type SkillsConfig = {
  options: string[];
  disabled: string[];
  updatedAt: IsoDateTime;
  updatedBy: string | null;
};

// -- Broadcast messaging ------------------------------------------------------

export type MessageChannel = 'push' | 'email';

/** Whether a channel can be used at all, and what is missing if not. */
export type ChannelStatus = {
  channel: MessageChannel;
  configured: boolean;
  /** API environment variables still to be set. */
  missing: string[];
  hint: string | null;
};

export type MessageAudience = {
  role: 'members' | 'coaches' | 'all';
  /** Omitted means every status the platform will contact. */
  status?: 'active' | 'pending';
  /** Beats the filter when present. */
  uids?: string[];
};

/**
 * What a filter actually resolves to.
 *
 * `total` is the audience; `reachable` is how many of them this channel can
 * physically reach. The two differ a lot - a member with no registered device
 * cannot receive a push however correct the filter was - and showing only the
 * first number is how somebody sends to "1,240 members" and reaches 300.
 */
export type AudiencePreview = {
  total: number;
  reachable: { push: number; email: number };
  sample: { uid: string; name: string; email: string | null; phoneNumber: string | null }[];
};

export type CampaignStatus = 'sending' | 'sent' | 'partial' | 'failed';

export type CampaignSummary = {
  id: string;
  channel: MessageChannel;
  title: string;
  body: string;
  audienceLabel: string;
  status: CampaignStatus;
  recipients: number;
  delivered: number;
  failed: number;
  sentByUid: string;
  sentByName: string;
  createdAt: IsoDateTime;
  completedAt: IsoDateTime | null;
};

export type SendCampaignInput = {
  channel: MessageChannel;
  /** Push heading or email subject. Required on both. */
  title: string;
  body: string;
  /** Push only. */
  imageUrl?: string;
  /** Push only. An in-app route, never an absolute URL. */
  deepLink?: string;
  audience: MessageAudience;
};

// -- Analytics ----------------------------------------------------------------

export type TimeSeriesPoint = {
  /** `YYYY-MM-DD`. */
  date: string;
  value: number;
};

export type AdminAnalytics = {
  kpis: {
    totalMembers: KpiValue;
    activeCoaches: KpiValue;
    pendingReviews: KpiValue;
    mrr: KpiValue;
  };
  signups: TimeSeriesPoint[];
  revenue: TimeSeriesPoint[];
  roleSplit: { role: 'members' | 'coaches'; count: number }[];
  needsAttention: {
    pendingApplications: {
      uid: string;
      name: string;
      expertise: string;
      submittedAt: IsoDateTime;
    }[];
    /** True count of pending coach applications - the Coach Applications sidebar badge. */
    pendingApplicationCount: number;
    /** True count of pending certificate/transformation/media reviews - the Review Queue sidebar badge. */
    reviewQueuePendingCount: number;
    openTickets: {
      id: string;
      subject: string;
      userName: string;
      createdAt: IsoDateTime;
    }[];
    /** True count of open tickets - the Tickets sidebar badge. */
    openTicketCount: number;
  };
};

export type KpiValue = {
  value: number;
  /** Change against the previous period, as a signed percentage. */
  deltaPercent: number | null;
};

// -- Discount codes -----------------------------------------------------------

/**
 * A promotional code, as the panel edits it.
 *
 * Mirrors `AdminCouponRow` in `Backend/src/modules/admin/admin.coupons.service.ts`.
 * `timesRedeemed` is read-only here and on the API: it is the count of real
 * redemptions and the only thing standing between a fifty-use code and its
 * fifty-first.
 */
export type AdminCouponRow = {
  /** Uppercase. Also the document id, which is what makes it unique. */
  code: string;
  description: string;
  kind: 'percent' | 'flat';
  /** Percent (1-100) or whole rupees off, per `kind`. */
  value: number;
  scope: 'all' | 'app_plan' | 'coach_plan';
  minOrderValue: number;
  /** Caps a percentage discount. 0 means uncapped. */
  maxDiscount: number;
  /** Total redemptions allowed. 0 means unlimited. */
  usageLimit: number;
  perUserLimit: number;
  timesRedeemed: number;
  expiresAt: IsoDateTime | null;
  active: boolean;
  createdAt: IsoDateTime | null;
};

export type CreateCouponBody = {
  code: string;
  description: string;
  kind: 'percent' | 'flat';
  value: number;
  scope: 'all' | 'app_plan' | 'coach_plan';
  minOrderValue: number;
  maxDiscount: number;
  usageLimit: number;
  perUserLimit: number;
  expiresAt: string | null;
};

export type UpdateCouponBody = Partial<Omit<CreateCouponBody, 'code'> & { active: boolean }>;
